import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, ExternalLink, Flag } from "lucide-react";
import SEO from "@/components/SEO";
import api from "@/lib/api";
import { CountryFlag } from "@/components/black1of1/CountryFlag";
import { FoilBody } from "@/components/black1of1/FoilBody";
import { countryOf } from "@/lib/countryFlags";
import {
  socialCtaFor,
  evidenceLabelFor,
  formatSurfacedDate,
} from "@/lib/black1of1Registry";

/**
 * /black-1of1/:sticker_number — Public detail page.
 *
 * Same visual language as the redesigned tracker: thick black frame,
 * silver-foil body, real SVG country flag as the hero when there is
 * no approved evidence image. Never fake a player face.
 */
export default function BlackOneOfOneDetail() {
  const { sticker_number } = useParams();
  const [p, setP] = useState(null);
  const [err, setErr] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(`/black-1of1/${encodeURIComponent(sticker_number)}`);
        if (!cancelled) setP(r.data);
      } catch (e) {
        if (!cancelled) setErr(e?.response?.data?.detail || "Not found");
      }
    })();
    return () => { cancelled = true; };
  }, [sticker_number]);
  const found = p?.status === "found";
  const showApprovedImage = found && !!p?.image_url;

  return (
    <div className="min-h-screen bg-[#0b0b0d] text-white" data-testid="black-1of1-detail">
      <SEO title={p ? `${p.sticker_number} · ${p.name} · Black 1/1 Tracker` : "Black 1/1"} />
      {/* Silver-foil ambience — matches tracker */}
      <div className="fixed inset-0 pointer-events-none opacity-[0.06]" aria-hidden style={{
        backgroundImage: 'url("/textures/silver-foil.svg")',
        backgroundSize: "800px 800px",
        backgroundRepeat: "repeat",
      }} />

      <div className="relative container-gm py-6 md:py-10">
        <Link
          to="/black-1of1"
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-white/80 hover:text-white mb-6 md:mb-8"
        >
          <ArrowLeft size={15} /> Back to Tracker
        </Link>

        {err ? (
          <div className="text-white/70 text-center py-16 text-lg">{err}</div>
        ) : !p ? (
          <div className="text-white/60 text-center py-16">Loading…</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_minmax(0,420px)] gap-8 md:gap-12 items-start">
            {/* ─── HERO CARD (mini 1/1 style, scaled up) ────────── */}
            <div
              className={[
                "relative rounded-2xl overflow-hidden aspect-[3/4] w-full",
                "bg-black p-[5px] md:p-[7px]",
                found
                  ? "shadow-[0_0_60px_-14px_rgba(34,197,94,0.65)]"
                  : "shadow-[0_10px_50px_-14px_rgba(0,0,0,0.9)]",
              ].join(" ")}
              data-testid="black-1of1-poster"
            >
              <div className="relative w-full h-full rounded-xl overflow-hidden">
                {showApprovedImage ? (
                  <img
                    src={p.image_url}
                    alt={p.name}
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                ) : found ? (
                  <FoilBody countryCode={p.country_code} />
                ) : (
                  <img
                    src="/textures/not-surfaced-card.webp"
                    alt="Not surfaced"
                    data-testid="black-1of1-detail-not-surfaced-img"
                    className="absolute inset-0 w-full h-full object-contain bg-black"
                  />
                )}
                {/* Bottom scrim ONLY — top stays silver */}
                <div className="absolute inset-x-0 bottom-0 h-[35%] bg-gradient-to-t from-black via-black/90 to-transparent pointer-events-none z-20" />

                {/* Status band */}
                <div className="absolute top-4 left-4 z-30">
                  {found ? (
                    <span className="inline-flex items-center gap-1.5 text-sm font-mono font-black uppercase tracking-widest bg-[#22c55e] text-black px-3 py-1.5 rounded-sm shadow-lg ring-1 ring-[#16a34a]">
                      <span className="w-1.5 h-1.5 rounded-full bg-black" /> Found
                    </span>
                  ) : (
                    <span className="inline-flex items-center text-sm font-mono font-black uppercase tracking-widest bg-black text-white px-3 py-1.5 rounded-sm ring-1 ring-white/60 shadow-lg">
                      Not Surfaced
                    </span>
                  )}
                </div>

                {showApprovedImage && (
                  <div className="absolute top-4 right-4 z-30">
                    <CountryFlag cc={p.country_code} className="w-10 h-7 rounded-sm ring-1 ring-black/70" />
                  </div>
                )}

                {/* Bottom stamp */}
                <div className="absolute inset-x-0 bottom-0 z-30 p-4 md:p-6">
                  <div className="text-xs md:text-sm font-mono uppercase tracking-[0.2em] text-[#d4af37] font-bold">
                    Panini FIFA World Cup 2026 · Black 1/1
                  </div>
                </div>
              </div>
            </div>

            {/* ─── META COLUMN ──────────────────────────────────── */}
            <div>
              <div className="inline-flex items-center gap-2 text-xs md:text-sm uppercase tracking-[0.22em] text-[#d4af37] font-mono font-bold mb-2">
                <CountryFlag cc={p.country_code} className="w-5 h-3.5 rounded-sm ring-1 ring-black/60" />
                {countryOf(p.country_code)} · {p.country_code}
              </div>
              <div className="font-mono text-base text-[#d4af37] mb-1 font-bold tracking-widest">{p.sticker_number}</div>
              <h1 className="font-display text-3xl md:text-5xl leading-[1.03] mb-2 font-black">{p.name}</h1>
              <div className="text-white/70 text-sm md:text-base mb-2 font-semibold">
                Panini FIFA World Cup 2026 · Black 1/1 Parallel
              </div>
              {showApprovedImage && p.photo_credit && (
                <div className="text-[12px] text-white/55 italic mb-4" data-testid="black-1of1-photo-credit">
                  Photo courtesy of {p.photo_credit}
                </div>
              )}
              {!showApprovedImage && found && (
                <div
                  className="mt-4 mb-6 rounded-lg border border-[#d4af37]/30 bg-[#d4af37]/[0.04] p-4"
                  data-testid="black-1of1-submit-photo-cta"
                >
                  <div className="text-[11px] font-mono uppercase tracking-[0.22em] text-[#d4af37] font-semibold mb-1">
                    Own this Black Border?
                  </div>
                  <p className="text-white/70 text-sm leading-relaxed mb-3">
                    Help us document the collection. Send us a clear photo of this 1 of 1.
                  </p>
                  <Link
                    to={`/black-1of1/report?sticker=${encodeURIComponent(p.sticker_number)}`}
                    data-testid="black-1of1-submit-photo-btn"
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#d4af37] hover:bg-[#f0c458] text-black text-xs font-bold uppercase tracking-widest transition"
                  >
                    Submit a photo
                  </Link>
                </div>
              )}

              {/* Msg 245 — the redesigned registry renders the status
                  itself (PUBLICLY SURFACED), so we only show a compact
                  status pill on the NOT-SURFACED branch. */}
              {!found && (
                <div className="mb-6">
                  <span className="inline-block text-sm px-4 py-2 rounded-sm bg-black text-white font-mono font-black uppercase tracking-widest ring-1 ring-white/60">
                    Not Surfaced
                  </span>
                </div>
              )}

              {/* Msg 245 · Premium provenance registry.
                  On FOUND rows: status header + collector + first-surfaced date + verification + documented-by + registry id.
                  On NOT-SURFACED rows: registry-styled placeholder + Report-a-Sighting CTA.
                  Layout uses subtle top-borders on each row block for a
                  registry-card look — never a database table. */}
              {found ? (
                <div
                  className="border-t border-white/15 pt-5 space-y-5"
                  data-testid="black-1of1-registry"
                >
                  {/* PUBLICLY SURFACED */}
                  <div data-testid="registry-status">
                    <div className="text-[10px] font-mono uppercase tracking-[0.28em] text-white/55 font-bold mb-1.5">
                      Status
                    </div>
                    <div className="flex items-center gap-2.5">
                      <span className="inline-block w-2 h-2 rounded-full bg-[#22c55e] shadow-[0_0_10px_rgba(34,197,94,0.65)]" />
                      <span className="font-display text-xl md:text-2xl text-white font-bold tracking-tight">
                        PUBLICLY SURFACED
                      </span>
                    </div>
                    {p.source_url && (
                      <div className="text-xs text-white/60 mt-1 leading-relaxed">
                        Verified through public evidence
                      </div>
                    )}
                  </div>

                  {/* KNOWN COLLECTOR */}
                  <RegistryBlock label="Known Collector" testid="registry-collector">
                    {p.owner_display_name ? (
                      <>
                        <div className="font-display text-lg md:text-xl text-white font-semibold break-words">
                          {p.owner_display_name}
                        </div>
                        {p.owner_social_url && (() => {
                          const s = socialCtaFor(p.owner_social_url);
                          if (!s) return null;
                          return (
                            <a
                              href={p.owner_social_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              data-testid="registry-collector-social"
                              className="mt-1.5 inline-flex items-center gap-1.5 text-[#d4af37] hover:text-[#f0c458] text-sm font-semibold underline-offset-4 hover:underline"
                            >
                              {s.cta} <ExternalLink size={13} />
                            </a>
                          );
                        })()}
                      </>
                    ) : (
                      <span className="text-white/55 italic">Owner unknown</span>
                    )}
                  </RegistryBlock>

                  {/* FIRST SURFACED */}
                  {p.first_surfaced_at && formatSurfacedDate(p.first_surfaced_at) && (
                    <RegistryBlock label="First Surfaced" testid="registry-first-surfaced">
                      <span className="font-display text-lg md:text-xl text-white font-semibold">
                        {formatSurfacedDate(p.first_surfaced_at)}
                      </span>
                    </RegistryBlock>
                  )}

                  {/* VERIFICATION + DOCUMENTED BY */}
                  {p.source_url && (() => {
                    const evLabel = evidenceLabelFor(p.source_url);
                    return (
                      <RegistryBlock label="Documented By" testid="registry-documented-by">
                        <div className="font-display text-lg md:text-xl text-white font-semibold">
                          {evLabel || "Public source"}
                        </div>
                        <a
                          href={p.source_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          data-testid="registry-evidence-link"
                          className="mt-1.5 inline-flex items-center gap-1.5 text-[#d4af37] hover:text-[#f0c458] text-sm font-semibold underline-offset-4 hover:underline break-all"
                        >
                          View original evidence <ExternalLink size={13} />
                        </a>
                      </RegistryBlock>
                    );
                  })()}

                  {/* REGISTRY ID */}
                  <RegistryBlock label="Registry ID" testid="registry-id">
                    <span className="font-mono text-lg md:text-xl text-white font-bold tracking-wider">
                      {p.sticker_number}
                    </span>
                  </RegistryBlock>
                </div>
              ) : (
                <div className="border-t border-white/15 pt-4 space-y-3 text-base text-white/85 leading-relaxed">
                  <p>
                    This one-of-one parallel has{" "}
                    <span className="text-white font-bold">not yet been publicly surfaced.</span>
                  </p>
                  <p className="text-white/70 text-sm md:text-base">
                    When verified public evidence of {p.name}&rsquo;s Black 1/1 surfaces, it will be documented here with the first publicly surfaced date, known owner when available, and source evidence.
                  </p>

                  <div className="mt-4 rounded-xl border-2 border-[#d4af37]/50 bg-[#d4af37]/[0.06] p-4 md:p-5" data-testid="black-1of1-detail-report-cta">
                    <div className="text-xs font-mono uppercase tracking-[0.24em] text-[#d4af37] mb-1 font-bold">
                      Seen this Black 1/1?
                    </div>
                    <div className="font-display text-xl text-white leading-tight mb-3 font-bold">
                      Report a sighting
                    </div>
                    <Link
                      to={`/black-1of1/report?sticker=${encodeURIComponent(p.sticker_number)}`}
                      data-testid="black-1of1-detail-report-btn"
                      className="inline-flex items-center gap-2 px-4 py-2.5 rounded-full bg-[#d4af37] hover:bg-[#f0c458] text-black text-sm font-bold"
                    >
                      <Flag size={14} /> Report a Sighting
                    </Link>
                    <div className="text-xs text-white/70 mt-2 font-mono font-semibold">
                      Verified before it appears on the public tracker.
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function RegistryBlock({ label, testid, children }) {
  return (
    <div className="border-t border-white/10 pt-4" data-testid={testid}>
      <div className="text-[10px] font-mono uppercase tracking-[0.28em] text-white/55 font-bold mb-1.5">
        {label}
      </div>
      <div className="text-white">{children}</div>
    </div>
  );
}
